// Lake surface index — the ONLY thing that makes water behave like water.
//
// swissALTI3D returns noise on open water, so the bake gates lakes flat
// (check_water, variance <= 0.1 m) and the DTM under a lake IS the water plane
// (measured: 1157 interior samples of St. Moritzersee, all 1767.90). With no
// collision of its own, water was therefore a solid floor: you walked across
// 1.58 km2 of lake with the sheet at your ankles, and every bridge was
// decorative because you could just wade the river.
//
// There is no bathymetry to stand on and inventing one would break the
// no-invented-terrain rule, so depth is not simulated. Instead a body inside a
// lake polygon FLOATS: it sits at the surface minus a fixed submersion, which
// reads as swimming and costs nothing in data.
//
// WATER-G: the water is drawn by the terrain shader (groundmat.js) on the
// DTM; a `sheet` lake (water.json, bake.py SHEET_MIN_M: a drawn-down body
// whose LiDAR caught the bed) by a water sheet at level_m (sky.js
// buildSheets). Either way the float stays level_m. Each lake also carries
// what the provider's facts give it (water.json lake `facts`, R12: max depth,
// area, shore, elevation, regime, its level gauge; absent = unknown), and the
// index holds the river gauges (water.json `gauges`). WATER-T: lakes and
// river reaches with a live source show its temperature (water.json
// lake.live / live_points + the game child's /live/water.json, refreshed
// daily server side; tempAt / tempOnRiver). This file also holds the pure
// water helpers the renderer uses:
// WaterAreas (point-in-polygon over water.json bodies) and flowField (the
// river flow the terrain's water texture carries).
import { fetchJsonOnce } from './fetchonce.js';
import { act } from './sched.js';

// even-odd crossing over every ring: a point on an island (a `holes` ring,
// bake rev 95 TLM ponds) crosses twice and is dry
function insideRings(rings, x, z) {
  let inside = false;
  for (let r = 0; r < rings.length; r++) {
    const p = rings[r];
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
      const pi = p[i], pj = p[j];                   // no destructuring: per frame
      const xi = pi[0], zi = pi[1], xj = pj[0], zj = pj[1];
      if ((zi > z) !== (zj > z)
        && x < ((xj - xi) * (z - zi)) / (zj - zi + 1e-12) + xi) inside = !inside;
    }
  }
  return inside;
}

export class WaterIndex {
  constructor(lakes, gauges, livePoints) {
    this.lakes = (lakes || []).map((l) => {
      const f = l.facts && typeof l.facts === 'object' ? l.facts : {};
      let x0 = 1e9, z0 = 1e9, x1 = -1e9, z1 = -1e9;
      for (const [x, z] of l.polygon) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
      return { level: l.level_m, name: l.name || '', poly: l.polygon,
        rings: [l.polygon, ...(l.holes || [])], bbox: [x0, z0, x1, z1], sheet: !!l.sheet,
        facts: f,
        depth: Number.isFinite(f.max_depth_m) && f.max_depth_m > 0 ? f.max_depth_m : null,
        live: Array.isArray(l.live) ? l.live.filter((i) => typeof i === 'string') : [] };
    });
    this.gauges = (gauges || []).filter((g) => g && g.river && Number.isFinite(g.x) && Number.isFinite(g.z));
    // WATER-T: the live temperature sources (water.json lake.live /
    // live_points) and the served values (/live/water.json, liveDoc)
    this.livePoints = (livePoints || []).filter((g) => g && typeof g.id === 'string' && g.river
      && Number.isFinite(g.x) && Number.isFinite(g.z));
    this.hasLive = this.livePoints.length > 0 || this.lakes.some((l) => l.live.length);
    this.liveDoc = null;
    this._liveAt = 0;
    this._liveBusy = false;
  }

  // WATER-T: fetch the served doc (game child /live/water.json) — at load and
  // then lazily, when a lookup finds it older than LIVE_REFETCH_MS (no timer)
  refreshLive(fetchFn = (u) => fetch(u, { cache: 'no-cache' })) {
    if (!this.hasLive || this._liveBusy) return Promise.resolve(this.liveDoc);
    this._liveBusy = true;
    this._liveAt = Date.now();
    return Promise.resolve().then(() => fetchFn(LIVE_URL))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((d) => { if (d && d.temps && typeof d.temps === 'object') this.liveDoc = d; return this.liveDoc; })
      .catch(() => this.liveDoc)
      .finally(() => { this._liveBusy = false; });
  }

  // the first of `ids` with a fresh value, else the first with any ->
  // liveTemp() record | null
  tempOf(ids, now = Date.now()) {
    if (!ids || !ids.length) return null;
    if (now - this._liveAt > LIVE_REFETCH_MS) this.refreshLive();
    const temps = (this.liveDoc && this.liveDoc.temps) || {};
    let any = null;
    for (const id of ids) {
      const t = liveTemp(temps[id], now);
      if (t && !t.stale) return t;
      if (t && !any) any = t;
    }
    return any;
  }

  // the lake's water temperature (lake.live) | null
  tempOfLake(lake, now) { return lake ? this.tempOf(lake.live, now) : null; }

  // the water temperature at (x, z): the lake there, else null
  tempAt(x, z, now) { return this.tempOfLake(this._at(x, z), now); }

  // river `name`'s temperature: its live point nearest (x, z) within
  // LIVE_RIVER_M (a station measures its reach, not the whole river) | null
  tempOnRiver(name, x, z, now) {
    let best = null, bd = LIVE_RIVER_M * LIVE_RIVER_M;
    for (const g of this.livePoints) {
      if (g.river !== name) continue;
      const d = (g.x - x) * (g.x - x) + (g.z - z) * (g.z - z);
      if (d <= bd) { bd = d; best = g; }
    }
    return best ? this.tempOf([best.id], now) : null;
  }

  _at(x, z) {
    for (const l of this.lakes) {
      const bb = l.bbox;
      if (x < bb[0] || x > bb[2] || z < bb[1] || z > bb[3]) continue;
      if (insideRings(l.rings, x, z)) return l;
    }
    return null;
  }

  // the lake record at (x, z) — { level, name, sheet, facts (water.json, {}
  // when none), depth (m | null) } — or null on dry land
  lakeAt(x, z) { return this._at(x, z); }

  // the lake's known maximum depth in m at (x, z), or null (dry land, or no
  // source knows it — never 0, never invented)
  depthAt(x, z) {
    const l = this._at(x, z);
    return l ? l.depth : null;
  }

  // water surface height at (x,z), or null on dry land
  surfaceAt(x, z) {
    const l = this._at(x, z);
    return l ? l.level : null;
  }

  // lake name at (x,z), or null on dry land
  nameAt(x, z) {
    const l = this._at(x, z);
    return l ? l.name : null;
  }

  // the gauge on river `name` nearest (x, z) — { nr, name, url, river, x, z }
  // — or null (the bake joined it to that way by geometry, bake.py
  // _join_water_facts)
  gaugeOn(name, x, z) {
    let best = null, bd = Infinity;
    for (const g of this.gauges) {
      if (g.river !== name) continue;
      const d = (g.x - x) * (g.x - x) + (g.z - z) * (g.z - z);
      if (d < bd) { bd = d; best = g; }
    }
    return best;
  }
}

// WATER-T: the live water doc (PROTOCOL.md "Live water"), its lookups and
// labels. A measured value is the station's last; a modelled one the series
// point nearest now (pickNow, the twin of server/waterlive.js pickNow).
const LIVE_URL = '/live/water.json';
const LIVE_REFETCH_MS = 3600000;      // a lookup refetches a doc older than this
const LIVE_RIVER_M = 3000;            // a station's temperature names this much of its river
const LIVE_STALE_MS = 2 * 86400000;
const LIVE_MODEL_GAP_MS = 6 * 3600000; // a modelled point this far from now is stale

export function pickNow(series, now) {
  let best = null, bd = Infinity;
  for (const p of series || []) {
    const d = Math.abs(p[0] - now);
    if (d < bd) { bd = d; best = p; }
  }
  return best;
}

// one /live/water.json temps entry -> { c (°C), at (ms), label, src,
// depth_m?, stale } | null (unknown: show nothing)
export function liveTemp(e, now = Date.now()) {
  if (!e || typeof e !== 'object') return null;
  let c = null, at = null;
  if (Array.isArray(e.series)) {
    const p = pickNow(e.series, now);
    if (p && Number.isFinite(p[0]) && Number.isFinite(p[1])) { at = p[0]; c = p[1]; }
  } else if (Number.isFinite(e.v) && Number.isFinite(e.t)) { c = e.v; at = e.t; }
  if (c == null) return null;
  const stale = !!e.stale || (Array.isArray(e.series) ? Math.abs(now - at) > LIVE_MODEL_GAP_MS : now - at > LIVE_STALE_MS);
  return { c, at, label: String(e.label || ''), src: e.series ? 'model' : 'measured',
    depth_m: Number.isFinite(e.depth_m) ? e.depth_m : null, stale };
}

const pad2 = (n) => String(n).padStart(2, '0');
// a time for the card: today "10:10", yesterday "yesterday 10:10", older the date
export function fmtLiveWhen(at, now = Date.now()) {
  const d = new Date(at), n = new Date(now);
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dd = Math.round((day(n) - day(d)) / 86400000);
  if (dd === 0 || dd === -1) return dd === 0 ? hm : `tomorrow ${hm}`;
  if (dd === 1) return `yesterday ${hm}`;
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}

// the aim label part: "12 °C" (fresh values only)
export function fmtTempShort(t) { return t && !t.stale ? `${Math.round(t.c)} °C` : ''; }

// the inspect card row: "12.1 °C (Eawag model, 1 m, 10:00)" /
// "5.2 °C (BAFU, measured 10:50)" / "… (BAFU, measured yesterday 10:50)";
// a stale value adds ", stale"
export function fmtTempRow(t, now = Date.now()) {
  if (!t) return '';
  const when = fmtLiveWhen(t.at, now);
  const src = t.src === 'model'
    ? `${t.label}${t.depth_m != null ? `, ${fmtDepth(t.depth_m)} m` : ''}, ${when}`
    : `${t.label}, measured ${when}`;
  return `${t.c.toFixed(1)} °C (${src}${t.stale ? ', stale' : ''})`;
}

// a known depth for a label: whole metres as such, else one decimal
export function fmtDepth(d) { return Number.isInteger(d) ? String(d) : d.toFixed(1); }

// River ribbons are NOT lakes: no float (the Inn/Ova channels are walkable
// riverbeds by design, APP_PLAYER.md §Transit + water), but nothing should GROW mid-stream.
// Segment grid over rivers.json ways; wet(x,z) = within half the drawn ribbon
// width (+ a tuft-base margin). Used by the grass scatter only (class-254
// water has no grass anyway: grass.js skips it).
// nameAt (WATER-G, R11): the river's name answers across the FULL painted
// width — inside a river area (`areas`, a WaterAreas of water.json
// river_areas) the nearest named way within AREA_NAME_M names it; outside,
// within 4 x the ribbon half width as before.
const AREA_NAME_M = 150;
export class RiverIndex {
  constructor(ways, areas) {
    this.areas = areas || null;
    this.cell = 64;
    this.grid = new Map();
    for (const w of ways || []) {
      const half = (w.w || 4) / 2 + 0.6;
      const name = w.name || '';
      const pts = w.pts || [];
      for (let i = 0; i + 1 < pts.length; i++) {
        const s = { ax: pts[i][0], az: pts[i][2], bx: pts[i + 1][0], bz: pts[i + 1][2], half, name };
        const x0 = Math.min(s.ax, s.bx) - half, x1 = Math.max(s.ax, s.bx) + half;
        const z0 = Math.min(s.az, s.bz) - half, z1 = Math.max(s.az, s.bz) + half;
        for (let cz = Math.floor(z0 / this.cell); cz <= Math.floor(z1 / this.cell); cz++) {
          for (let cx = Math.floor(x0 / this.cell); cx <= Math.floor(x1 / this.cell); cx++) {
            const k = cx + '_' + cz;
            const cell = this.grid.get(k);
            if (cell) cell.push(s); else this.grid.set(k, [s]);
          }
        }
      }
    }
  }

  wet(x, z) {
    const cell = this.grid.get(Math.floor(x / this.cell) + '_' + Math.floor(z / this.cell));
    if (!cell) return false;
    for (const s of cell) {
      const dx = s.bx - s.ax, dz = s.bz - s.az;
      const t = Math.max(0, Math.min(1,
        ((x - s.ax) * dx + (z - s.az) * dz) / (dx * dx + dz * dz + 1e-12)));
      if (Math.hypot(x - (s.ax + dx * t), z - (s.az + dz * t)) < s.half) return true;
    }
    return false;
  }

  // river name at (x,z), or '' — near a ribbon (3×3 cells, 4 x its half
  // width), else anywhere inside a river area (the nearest named way)
  nameAt(x, z) {
    const near = this._nearest(x, z, 1, (s, d) => d < s.half * 4);
    if (near || !this.areas || !this.areas.inside(x, z)) return near;
    return this._nearest(x, z, Math.ceil(AREA_NAME_M / this.cell), (s, d) => d < AREA_NAME_M);
  }

  _nearest(x, z, r, accept) {
    const cx0 = Math.floor(x / this.cell), cz0 = Math.floor(z / this.cell);
    let best = '', bd = Infinity;
    for (let cx = cx0 - r; cx <= cx0 + r; cx++) {
      for (let cz = cz0 - r; cz <= cz0 + r; cz++) {
        const cell = this.grid.get(cx + '_' + cz);
        if (!cell) continue;
        for (const s of cell) {
          if (!s.name) continue;
          const dx = s.bx - s.ax, dz = s.bz - s.az;
          const t = Math.max(0, Math.min(1,
            ((x - s.ax) * dx + (z - s.az) * dz) / (dx * dx + dz * dz + 1e-12)));
          const d = Math.hypot(x - (s.ax + dx * t), z - (s.az + dz * t));
          if (d < bd && accept(s, d)) { bd = d; best = s.name; }
        }
      }
    }
    return best;
  }
}

// ------------------------------------------------------------- WaterAreas
// Point-in-polygon over water.json bodies (lakes, river_areas: the very
// polygons bake.water_stamp_mask stamps as class 254), even-odd over every
// ring (islands dry). Edges are bucketed in BAND_M rows of z, so a query
// tests only the edges of its band.
// R8d2: the bucketing is a STEP GENERATOR (addSteps: a step per body and per
// AREA_STEP ring edges; the live bake's river areas are 22k edges, one body
// 14k, ~25 ms in one go); WaterAreas.build(bodies) runs it from empty (sky.js
// buildSheets / buildRivers, decode.js runSteps), the constructor drains it —
// the same polys, the same bands, the same order.
const BAND_M = 32;
const AREA_STEP = 4096;
export class WaterAreas {
  constructor(bodies) {
    this.polys = [];
    const it = this.addSteps(bodies);
    while (!it.next().done);
  }

  static *build(bodies) {
    const a = new WaterAreas(null);
    yield* a.addSteps(bodies);
    return a;
  }

  *addSteps(bodies) {
    let n = 0;
    for (const b of bodies || []) {
      if (!b || !Array.isArray(b.polygon) || b.polygon.length < 3) continue;
      const rings = [b.polygon, ...(b.holes || [])].filter((r) => Array.isArray(r) && r.length >= 3);
      let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
      for (const [x, z] of b.polygon) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
      const b0 = Math.floor(z0 / BAND_M), nb = Math.floor(z1 / BAND_M) - b0 + 1;
      const bands = Array.from({ length: nb }, () => []);
      for (const r of rings) {
        for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
          const za = r[j][1], zb = r[i][1];
          const lo = Math.max(0, Math.floor(Math.min(za, zb) / BAND_M) - b0);
          const hi = Math.min(nb - 1, Math.floor(Math.max(za, zb) / BAND_M) - b0);
          const e = [r[j][0], za, r[i][0], zb];
          for (let k = lo; k <= hi; k++) bands[k].push(e);
          if (++n % AREA_STEP === 0) yield;
        }
      }
      this.polys.push({ bbox: [x0, z0, x1, z1], b0, bands, body: b });
      yield;                                   // the next body
    }
  }

  // the body (water.json object) at (x, z), or null
  at(x, z) {
    for (const p of this.polys) {
      const bb = p.bbox;
      if (x < bb[0] || x > bb[2] || z < bb[1] || z > bb[3]) continue;
      const band = p.bands[Math.floor(z / BAND_M) - p.b0];
      if (!band) continue;
      let inside = false;
      for (let i = 0; i < band.length; i++) {
        const e = band[i];
        if ((e[1] > z) !== (e[3] > z) && x < ((e[2] - e[0]) * (z - e[1])) / (e[3] - e[1] + 1e-12) + e[0]) inside = !inside;
      }
      if (inside) return p.body;
    }
    return null;
  }

  inside(x, z) { return this.at(x, z) !== null; }
}

// ------------------------------------------------------------- flowField
// WATER-G R6: the river flow over class-254 river areas, for the terrain's
// water texture (groundmat setWaterFlow rasterises it once at load). Inside
// a river area: the nearest rivers.json way within FLOW_NEAR_M (a stream
// counts STREAM_PENALTY_M farther than a river), its downstream tangent (the
// points run downstream) x the ribbon rule's speed, 0.12 + 4.5 sqrt(slope)
// m/s capped at 4 (slope over the segment's neighbours). Elsewhere (lakes,
// ponds, any other water): null — the shader's wind drift alone.
// R8d2: the segment grid is a STEP GENERATOR (flowFieldSteps: a step per
// FLOW_STEP segments; the live bake's 45k segments were a ~190 ms task under
// water.rivers), run by sky.js buildRivers (runSteps); flowField = its drain,
// the same grid in the same order. The sampler builds nothing on its first
// call (no lazy index: WaterAreas buckets at build).
export const FLOW_VMAX = 4;
const FLOW_NEAR_M = 80, STREAM_PENALTY_M = 15, FLOW_CELL = 64;
const FLOW_STEP = 512;
export function riverSpeed(slope) { return Math.min(FLOW_VMAX, 0.12 + 4.5 * Math.sqrt(Math.max(0, slope))); }
export function flowField(ways, riverAreas) {
  const it = flowFieldSteps(ways, riverAreas);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* flowFieldSteps(ways, riverAreas) {
  const areas = riverAreas instanceof WaterAreas ? riverAreas : yield* WaterAreas.build(riverAreas);
  const grid = new Map();
  let n = 0;
  for (const w of ways || []) {
    const pts = w.pts || [];
    const pen = w.kind === 'river' ? 0 : STREAM_PENALTY_M;
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 2)];
      const len = Math.hypot(b[0] - a[0], b[2] - a[2]) || 1;
      const v = riverSpeed(Math.max(0, a[1] - b[1]) / len);
      const ax = pts[i][0], az = pts[i][2], bx = pts[i + 1][0], bz = pts[i + 1][2];
      const L = Math.hypot(bx - ax, bz - az);
      if (!(L > 1e-6)) continue;
      const s = { ax, az, bx, bz, ux: (bx - ax) / L, uz: (bz - az) / L, v, pen };
      const x0 = Math.min(ax, bx) - FLOW_NEAR_M, x1 = Math.max(ax, bx) + FLOW_NEAR_M;
      const z0 = Math.min(az, bz) - FLOW_NEAR_M, z1 = Math.max(az, bz) + FLOW_NEAR_M;
      for (let cz = Math.floor(z0 / FLOW_CELL); cz <= Math.floor(z1 / FLOW_CELL); cz++) {
        for (let cx = Math.floor(x0 / FLOW_CELL); cx <= Math.floor(x1 / FLOW_CELL); cx++) {
          const k = cx + '_' + cz;
          const c = grid.get(k);
          if (c) c.push(s); else grid.set(k, [s]);
        }
      }
      if (++n % FLOW_STEP === 0) yield;
    }
  }
  return (x, z) => {
    if (!areas.inside(x, z)) return null;
    const cell = grid.get(Math.floor(x / FLOW_CELL) + '_' + Math.floor(z / FLOW_CELL));
    if (!cell) return null;
    let best = null, bd = Infinity;
    for (const s of cell) {
      const dx = s.bx - s.ax, dz = s.bz - s.az;
      const t = Math.max(0, Math.min(1, ((x - s.ax) * dx + (z - s.az) * dz) / (dx * dx + dz * dz + 1e-12)));
      const d = Math.hypot(x - (s.ax + dx * t), z - (s.az + dz * t));
      if (d > FLOW_NEAR_M) continue;
      if (d + s.pen < bd) { bd = d + s.pen; best = s; }
    }
    return best ? [best.ux * best.v, best.uz * best.v] : null;
  };
}

export async function loadWater(baseUrl) {
  try {
    // one fetch, shared with the stream ribbons + flow (sky.js buildRivers)
    const w = await fetchJsonOnce(`${baseUrl}/water.json`);
    if (!w || !w.lakes) return null;
    act('water.index');                   // the long-task label of the rest of this task (sched.js)
    const idx = new WaterIndex(w.lakes, w.gauges, w.live_points);
    idx.refreshLive();                    // WATER-T: no live source = no request
    return idx;
  } catch { return null; }
}
